import { configureStore, createSlice } from '@reduxjs/toolkit';
// Import Thunks from original propertiesSlice
import { fetchRegionGroups, fetchDepartureStations, fetchAgency, fetchHolidays } from '@reducers/propertiesSlice';
// Import utility functions from utils.js (COMPLETE airport mapping!)
import { airportcity, airportregions } from '@reducers/utils';

// ============================================
// INLINE SLICES - Keine Slice-Import circular deps!
// ============================================

// System Slice (minimal für Widget)
const systemSlice = createSlice({
  name: 'system',
  initialState: {
    sessionId: '',
    view: 'widget',
    tenantID: '',
    language: 'de',
    premiumSearch: false,
  },
  reducers: {
    setSessionId: (state, action) => {
      state.sessionId = action.payload;
    },
  },
});

// Search Slice (minimal für Widget)
const searchSlice = createSlice({
  name: 'search',
  initialState: {
    searchRegions: [],
    searchAirports: [],
    searchDate: null,
    searchPersons: {
      adults: 2,
      children: 0,
      childrenAges: [],
      rooms: 1,
    },
    selectedDestination: [],
    duration: null,
    adults: null,
    children: null,
    childrenages: [],
    travelDate: null,
    stations: [],
    desiredHotels: [],
    multiroomdivider: [],
    preferredCategory: '',
    activeMode: 'PAC',
  },
  reducers: {
    setSearchOption: (state, action) => {
      const { property, data } = action.payload;
      state[property] = data;
    },
    setSelectedDestination: (state, action) => {
      state.selectedDestination = action.payload;
    },
    setPreferredCategory: (state, action) => {
      state.preferredCategory = action.payload;
    },
    setActiveMode: (state, action) => {
      state.activeMode = action.payload;
    },
  },
});

// Properties Slice (minimal BUT with Thunk support!)
const propertiesSlice = createSlice({
  name: 'properties',
  initialState: {
    regionGroups: [],
    departureStations: [],
    airlines: [],
    agencyproperties: {},  // ✅ ADD: For fetchAgency response
    holidays: { publicHolidays: {}, schoolHolidays: {}, states: [] },  // ✅ ADD: For fetchHolidays
  },
  reducers: {
    setRegionGroups: (state, action) => {
      state.regionGroups = action.payload;
    },
    setDepartureStations: (state, action) => {
      state.departureStations = action.payload;
    },
  },
  // ✅ ADD extraReducers for Thunk support!
  extraReducers: (builder) => {
    builder.addCase(fetchRegionGroups.pending, (state) => {
      state.regionGroups = [];
    });
    builder.addCase(fetchRegionGroups.fulfilled, (state, { payload }) => {
      state.regionGroups = payload.RegionGroups.RegionGroup;
    });
    builder.addCase(fetchDepartureStations.pending, (state) => {
      state.departureStations = [];
    });
    builder.addCase(fetchDepartureStations.fulfilled, (state, { payload }) => {
      // ✅ Use imported functions from utils.js (COMPLETE airport list!)
      const dep = payload.Stations.DepartureStations.map((e) => {
        return { value: e, label: airportcity(e) };
      });

      state.departureStations = [...airportregions(), ...dep];
    });
    // ✅ ADD: fetchAgency handlers
    builder.addCase(fetchAgency.fulfilled, (state, { payload }) => {
      console.log('[Widget Store] fetchAgency.fulfilled:', payload);
      state.agencyproperties = payload;
    });
    builder.addCase(fetchAgency.rejected, (state, { payload }) => {
      console.error('[Widget Store] fetchAgency.rejected:', payload);
    });
    // ✅ ADD: fetchHolidays handlers
    builder.addCase(fetchHolidays.fulfilled, (state, { payload }) => {
      // Transformiere API-Daten in das Format das DatePopUp erwartet
      const publicHolidays = {};
      const schoolHolidays = {};

      for (const holiday of payload.holidays) {
        const year = holiday.year?.toString();
        const holidayType = holiday.holiday_type;

        if (holidayType === 'public') {
          if (!publicHolidays[year]) {
            publicHolidays[year] = [];
          }
          const states = (holiday.iso_codes || []).map(code =>
            code === 'ALL' ? 'ALL' : code.replace('DE-', '')
          );
          publicHolidays[year].push({
            date: holiday.start_date,
            name: holiday.holiday_name || holiday.label,
            states: states.length > 0 ? states : ['ALL']
          });
        } else if (holidayType === 'school') {
          for (const isoCode of (holiday.iso_codes || [])) {
            const shortCode = isoCode.replace('DE-', '');
            if (!schoolHolidays[shortCode]) {
              schoolHolidays[shortCode] = [];
            }
            // Deduplizierung: Prüfe ob diese ID bereits existiert
            const exists = schoolHolidays[shortCode].some(
              (h) => h.id === holiday.id
            );
            if (!exists) {
              schoolHolidays[shortCode].push({
                id: holiday.id,
                start: holiday.start_date,
                end: holiday.end_date,
                name: holiday.holiday_name || holiday.label
              });
            }
          }
        }
      }

      const states = payload.states.map(s => ({
        code: s.iso_code?.replace('DE-', '') || s.code,
        name: s.label || s.name
      }));

      state.holidays = { publicHolidays, schoolHolidays, states };
    });
  },
});

// Offers Slice (minimal — MainInput liest alternativeRegionName)
const offersSlice = createSlice({
  name: 'offers',
  initialState: {
    alternativeRegionName: '',
  },
  reducers: {},
});

// Language Slice
const languageSlice = createSlice({
  name: 'language',
  initialState: {
    current: 'de',
  },
  reducers: {
    setLanguage: (state, action) => {
      state.current = action.payload;
    },
  },
});

// Export actions for use in components
export const { setSessionId } = systemSlice.actions;
export const { setSearchOption, setSelectedDestination, setPreferredCategory, setActiveMode } = searchSlice.actions;
export const { setRegionGroups, setDepartureStations } = propertiesSlice.actions;
export const { setLanguage } = languageSlice.actions;

// Export selectors that FrontSearchInputs needs
export const selectSearchFilters = (state) => ({
  searchRegions: state.search.searchRegions,
  searchAirports: state.search.searchAirports,
  searchDate: state.search.searchDate,
  searchPersons: state.search.searchPersons,
});

export const completeSearch = (state) => state.search;

// Helper function for API host
// ✅ __DEFAULT_API_URL__ wird von Vite bei Build ersetzt (siehe vite.config.js)
export function getHost() {
  return window.configData?.API_URL || __DEFAULT_API_URL__;
}

/**
 * Create Redux Store for Widget - MINIMAL VERSION with Thunk Support
 * @param {object} initialState - Initial state
 * @param {string} initialState.sessionId - Session UUID
 * @param {string} initialState.tenantID - Tenant UUID
 * @param {string} initialState.language - Language code
 * @param {boolean} initialState.premiumSearch - Enable premium search badges
 */
export function createWidgetStore(initialState = {}) {
  const {
    sessionId = '',
    tenantID = '',
    language = 'de',
    premiumSearch = false,
  } = initialState;

  const store = configureStore({
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false,
      }),
    
    reducer: {
      system: systemSlice.reducer,
      properties: propertiesSlice.reducer,
      search: searchSlice.reducer,
      offers: offersSlice.reducer,
      language: languageSlice.reducer,
    },
    
    preloadedState: {
      system: {
        sessionId: sessionId,
        view: 'widget',
        tenantID: tenantID,
        language: language,
        premiumSearch: premiumSearch,
      },
      language: {
        current: language,
      },
    },
  });

  return store;
}

export default createWidgetStore;